/* ============================================================
   GW · Cabecera píldora
   La barra va siempre abierta y flotando sobre el contenido.
   Aquí vive: las hojas inferiores (menú y carrito), el gesto
   para arrastrarlas, la rotación de anuncios, el color del logo
   y los iconos según el fondo (barra transparente) y el carrito
   (envío gratis, resumen, código de descuento, cambio de
   talla y recomendaciones).
   Custom element: se reinicia solo cuando el editor de temas
   vuelve a renderizar la sección.
   ============================================================ */

if (!customElements.get('gwh-header')) {
  const ICONO_PAPELERA =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M4 7h16M10 11v6M14 11v6M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13M9 7V4h6v3"/></svg>';

  class GwhHeader extends HTMLElement {
    connectedCallback() {
      this.menosMovimiento = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.moneda = this.dataset.moneda || 'EUR';
      this.idioma = this.dataset.idioma || 'es-ES';
      this.raiz = this.dataset.raiz || '/';

      this.velo = this.querySelector('[data-gwh-velo]');
      this.hojas = Array.from(this.querySelectorAll('[data-gwh-hoja]'));
      this.hojaAbierta = null;
      this.abridor = null;                       // a quién devolvemos el foco

      this.alTeclear = this.alTeclear.bind(this);
      this.refrescarCarrito = this.refrescarCarrito.bind(this);

      this.iniciarHojas();
      this.iniciarMenu();
      this.iniciarCarrito();
      this.iniciarCodigo();
      this.iniciarAnuncios();
      this.iniciarIdioma();
      this.iniciarTono();

      document.addEventListener('keydown', this.alTeclear);
      document.addEventListener('cart:update', this.refrescarCarrito);
      document.addEventListener('cart:refresh', this.refrescarCarrito);

      this.iniciarRueda();
    }

    /* En escritorio Horizon desplaza .page-wrapper, no la ventana. La barra es
       position: fixed, así que la rueda del ratón encima de ella iba a la
       ventana (bloqueada) y la página no se movía: se la pasamos al wrapper. */
    iniciarRueda() {
      this.addEventListener(
        'wheel',
        (e) => {
          if (this.hojaAbierta || e.ctrlKey || e.target.closest('[data-gwh-hoja]')) return;

          const wrapper = document.querySelector('.page-wrapper');
          if (!wrapper || wrapper.scrollHeight <= wrapper.clientHeight) return;

          const estilo = getComputedStyle(wrapper).overflowY;
          if (estilo !== 'auto' && estilo !== 'scroll') return;

          // Lo hacemos nosotros y anulamos el del navegador: así ninguno desplaza dos veces
          e.preventDefault();
          const factor = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? wrapper.clientHeight : 1;
          wrapper.scrollBy({ top: e.deltaY * factor, left: 0, behavior: 'instant' });
        },
        { passive: false }
      );
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.alTeclear);
      document.removeEventListener('cart:update', this.refrescarCarrito);
      document.removeEventListener('cart:refresh', this.refrescarCarrito);
      this.pararAnuncios();
      this.pararTono();
      this.bloquearScroll(false);
    }

    /* Horizon, a partir de 990 px, no desplaza la ventana sino .page-wrapper,
       así que el overflow del body no basta: su atributo scroll-lock bloquea las dos. */
    bloquearScroll(activo) {
      document.body.style.overflow = activo ? 'hidden' : '';
      document.documentElement.toggleAttribute('scroll-lock', activo);
    }

    /* ============================================================
       HOJAS
       ============================================================ */

    iniciarHojas() {
      // Abridores: cualquier elemento con data-gwh-abre="menu|carrito"
      this.querySelectorAll('[data-gwh-abre]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.abrir(btn.dataset.gwhAbre, btn);
        });
      });

      this.velo?.addEventListener('click', () => this.cerrar());

      this.hojas.forEach((hoja) => {
        hoja.querySelectorAll('[data-gwh-cierra]').forEach((btn) => {
          btn.addEventListener('click', () => this.cerrar());
        });
        this.iniciarGesto(hoja);
      });
    }

    abrir(nombre, abridor) {
      const hoja = this.hojas.find((h) => h.dataset.gwhHoja === nombre);
      if (!hoja) return;

      if (this.hojaAbierta && this.hojaAbierta !== hoja) this.cerrar(true);

      this.abridor = abridor || null;
      this.hojaAbierta = hoja;

      hoja.classList.add('abierta');
      hoja.removeAttribute('inert');
      this.velo?.classList.add('visible');
      this.classList.add('gwh--hoja-abierta');
      this.bloquearScroll(true);

      if (abridor) abridor.setAttribute('aria-expanded', 'true');

      if (nombre === 'carrito') this.cargarCarrito();

      // El foco entra en la hoja
      const foco = hoja.querySelector('[data-gwh-cierra]');
      window.requestAnimationFrame(() => foco && foco.focus({ preventScroll: true }));

      this.pararAnuncios();
    }

    cerrar(silencioso) {
      if (!this.hojaAbierta) return;

      const hoja = this.hojaAbierta;
      hoja.classList.remove('abierta');
      hoja.style.transform = '';
      hoja.setAttribute('inert', '');

      this.hojaAbierta = null;

      if (!silencioso) {
        this.velo?.classList.remove('visible');
        this.classList.remove('gwh--hoja-abierta');
        this.bloquearScroll(false);
        this.arrancarAnuncios();
      }

      this.querySelectorAll('[data-gwh-abre]').forEach((b) => b.setAttribute('aria-expanded', 'false'));

      if (this.abridor && !silencioso) {
        this.abridor.focus({ preventScroll: true });
        this.abridor = null;
      }
    }

    /* Arrastrar la hoja hacia abajo para cerrarla */
    iniciarGesto(hoja) {
      const asa = hoja.querySelector('[data-gwh-asa]');
      if (!asa) return;

      let inicioY = 0;
      let desplazamiento = 0;
      let arrastrando = false;
      let tiempo = 0;

      const empezar = (e) => {
        arrastrando = true;
        inicioY = e.clientY;
        desplazamiento = 0;
        tiempo = e.timeStamp;
        hoja.classList.add('arrastrando');
        asa.setPointerCapture(e.pointerId);
      };

      const mover = (e) => {
        if (!arrastrando) return;
        desplazamiento = Math.max(0, e.clientY - inicioY);   // solo hacia abajo
        hoja.style.transform = 'translateY(' + desplazamiento + 'px)';
      };

      const soltar = (e) => {
        if (!arrastrando) return;
        arrastrando = false;
        hoja.classList.remove('arrastrando');
        if (asa.hasPointerCapture(e.pointerId)) asa.releasePointerCapture(e.pointerId);

        const velocidad = desplazamiento / Math.max(e.timeStamp - tiempo, 1);
        const recorrido = desplazamiento / Math.max(hoja.offsetHeight, 1);

        if (recorrido > 0.28 || velocidad > 0.6) this.cerrar();
        else hoja.style.transform = '';
      };

      asa.addEventListener('pointerdown', empezar);
      asa.addEventListener('pointermove', mover);
      asa.addEventListener('pointerup', soltar);
      asa.addEventListener('pointercancel', soltar);
    }

    alTeclear(e) {
      if (e.key === 'Escape' && this.hojaAbierta) this.cerrar();
    }

    /* ============================================================
       MENÚ (submenús en acordeón)
       ============================================================ */

    iniciarMenu() {
      const menu = this.querySelector('[data-gwh-hoja="menu"]');
      if (!menu) return;

      menu.addEventListener('click', (e) => {
        const sub = e.target.closest('[data-gwh-sub]');
        if (sub) {
          const abierto = sub.getAttribute('aria-expanded') === 'true';
          sub.setAttribute('aria-expanded', String(!abierto));
          const lista = sub.nextElementSibling;
          if (lista) lista.classList.toggle('abierto', !abierto);
          return;
        }
        if (e.target.closest('a')) this.cerrar();
      });
    }

    /* ============================================================
       CARRITO
       ============================================================ */

    iniciarCarrito() {
      this.hojaCarrito = this.querySelector('[data-gwh-hoja="carrito"]');
      if (!this.hojaCarrito) return;

      this.lista = this.hojaCarrito.querySelector('[data-gwh-lista]');
      this.pie = this.hojaCarrito.querySelector('[data-gwh-pie]');
      this.resumen = this.hojaCarrito.querySelector('[data-gwh-resumen]');
      this.contadorTitulo = this.hojaCarrito.querySelector('[data-gwh-cuenta]');

      this.envio = this.hojaCarrito.querySelector('[data-gwh-envio]');
      this.envioTexto = this.hojaCarrito.querySelector('[data-gwh-envio-texto]');
      this.envioFill = this.hojaCarrito.querySelector('[data-gwh-envio-fill]');

      this.nudge = this.hojaCarrito.querySelector('[data-gwh-nudge]');
      this.nudgeTexto = this.hojaCarrito.querySelector('[data-gwh-nudge-texto]');

      // Los rótulos del resumen y del envío viajan en un JSON, no en veinte data-*
      this.t = {};
      const textos = this.hojaCarrito.querySelector('[data-gwh-textos]');
      if (textos) {
        try { this.t = JSON.parse(textos.textContent) || {}; } catch (err) { this.t = {}; }
      }
      this.umbral = Math.round(Number(this.t.umbral || 0) * 100);

      this.lista?.addEventListener('click', (e) => {
        // Añadir una recomendación al carrito
        const add = e.target.closest('[data-gwh-add]');
        if (add) {
          this.anadir(add.dataset.gwhAdd, add);
          return;
        }

        // Más, menos y papelera
        const btn = e.target.closest('[data-gwh-cambia]');
        if (btn) {
          const linea = btn.closest('[data-linea]');
          if (!linea) return;
          this.cambiarLinea(Number(linea.dataset.linea), Number(btn.dataset.gwhCambia));
          return;
        }

        // Cambiar de talla o color sin salir del carrito
        const variante = e.target.closest('[data-gwh-variante]');
        if (variante) {
          this.abrirVariantes(variante);
          return;
        }

        // Al ir a una ficha (línea del carrito o recomendación) la hoja se cierra:
        // así al volver atrás no queda el scroll bloqueado.
        if (e.target.closest('a')) this.cerrar();
      });
    }

    dinero(centimos) {
      try {
        return new Intl.NumberFormat(this.idioma, {
          style: 'currency',
          currency: this.moneda,
        }).format(centimos / 100);
      } catch (err) {
        return (centimos / 100).toFixed(2) + ' ' + this.moneda;
      }
    }

    escapar(txt) {
      return String(txt == null ? '' : txt)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    cargarCarrito() {
      if (!this.lista) return;

      this.lista.innerHTML = '<p class="gwh__cargando">' + (this.dataset.textoCargando || 'Cargando…') + '</p>';

      fetch(this.raiz + 'cart.js', { headers: { Accept: 'application/json' } })
        // Una respuesta de error también es fallo: si no, se quedaba en «Cargando…» para siempre
        .then((r) => {
          if (!r.ok) throw new Error(r.status);
          return r.json();
        })
        .then((carrito) => this.pintarCarrito(carrito))
        .catch(() => {
          this.lista.innerHTML = '<p class="gwh__cargando">' + (this.dataset.textoError || 'No se ha podido cargar el carrito.') + '</p>';
        });
    }

    /* ---------- Envío gratis ---------- */

    pintarEnvio(carrito) {
      if (!this.envio) return;

      if (!this.umbral || !carrito.items.length) {
        this.envio.hidden = true;
        return;
      }

      this.envio.hidden = false;

      const avance = Math.min(1, carrito.total_price / this.umbral);
      const falta = Math.max(0, this.umbral - carrito.total_price);
      const logrado = falta === 0;

      if (this.envioFill) this.envioFill.style.width = (avance * 100).toFixed(1) + '%';

      this.envio.classList.toggle('completo', logrado);

      if (this.envioTexto) {
        this.envioTexto.innerHTML = logrado
          ? this.escapar(this.t.envioOk)
          : this.escapar(this.t.envioFalta).replace('{falta}', '<b>' + this.dinero(falta) + '</b>');
      }
    }

    /* ---------- Empujón de promoción por cantidad ----------
       Solo es el aviso: el descuento de verdad se crea en Shopify
       (Descuentos → automático), y cuando salta aparece ya en el resumen. */

    pintarNudge(carrito) {
      if (!this.nudge) return;

      const meta = Number(this.t.bundleCantidad || 0);

      if (!meta || !carrito.items.length || carrito.item_count >= meta) {
        this.nudge.hidden = true;
        return;
      }

      this.nudge.hidden = false;
      if (this.nudgeTexto) {
        this.nudgeTexto.textContent = String(this.t.bundleTexto || '').replace('{faltan}', meta - carrito.item_count);
      }
    }

    /* ---------- Resumen de compra ---------- */

    fila(etiqueta, valor, clase) {
      return (
        '<div class="gwh__fila' + (clase ? ' ' + clase : '') + '">' +
        '<span>' + this.escapar(etiqueta) + '</span>' +
        '<span>' + this.escapar(valor) + '</span>' +
        '</div>'
      );
    }

    pintarResumen(carrito) {
      if (!this.resumen) return;

      const filas = [this.fila(this.t.subtotal || 'Subtotal', this.dinero(carrito.original_total_price))];

      // Los descuentos de carrito vienen con su nombre; el resto (los de línea)
      // se agrupan en una sola fila para no repetir lo mismo cinco veces.
      let contado = 0;
      (carrito.cart_level_discount_applications || []).forEach((desc) => {
        contado += desc.total_allocated_amount;
        filas.push(this.fila(desc.title, '− ' + this.dinero(desc.total_allocated_amount), 'gwh__fila--ahorro'));
      });

      const resto = (carrito.total_discount || 0) - contado;
      if (resto > 0) {
        filas.push(this.fila(this.t.descuento || 'Descuento', '− ' + this.dinero(resto), 'gwh__fila--ahorro'));
      }

      if (this.umbral) {
        const gratis = carrito.total_price >= this.umbral;
        filas.push(
          this.fila(
            this.t.envio || 'Envío',
            gratis ? this.t.envioGratis || 'Gratis' : this.t.envioPendiente || '',
            gratis ? 'gwh__fila--gratis' : ''
          )
        );
      }

      filas.push(this.fila(this.t.total || 'Total', this.dinero(carrito.total_price), 'gwh__fila--total'));

      this.resumen.innerHTML = filas.join('');
    }

    /* ---------- Código de descuento ----------
       Shopify aplica el código visitando /discount/<codigo>. Se llama en segundo
       plano y luego se vuelve a pedir el carrito: si el ahorro no ha subido,
       ese código no vale para lo que lleva dentro. */

    iniciarCodigo() {
      if (!this.hojaCarrito) return;

      const form = this.hojaCarrito.querySelector('[data-gwh-codigo]');
      if (!form) return;

      this.codigoAviso = this.hojaCarrito.querySelector('[data-gwh-codigo-aviso]');

      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const campo = form.querySelector('input');
        const codigo = (campo ? campo.value : '').trim();
        if (codigo) this.aplicarCodigo(codigo, form);
      });
    }

    avisoCodigo(texto, bien) {
      if (!this.codigoAviso) return;
      this.codigoAviso.textContent = texto || '';
      this.codigoAviso.classList.toggle('ok', !!texto && bien);
      this.codigoAviso.classList.toggle('mal', !!texto && !bien);
      this.codigoAviso.hidden = !texto;
    }

    aplicarCodigo(codigo, form) {
      const boton = form.querySelector('button');
      if (boton) boton.disabled = true;
      this.avisoCodigo('', false);

      let ahorroAntes = 0;

      fetch(this.raiz + 'cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((carrito) => {
          ahorroAntes = carrito.total_discount || 0;
          return fetch(this.raiz + 'discount/' + encodeURIComponent(codigo), { credentials: 'same-origin' });
        })
        .then(() => fetch(this.raiz + 'cart.js', { headers: { Accept: 'application/json' } }))
        .then((r) => r.json())
        .then((carrito) => {
          this.pintarCarrito(carrito);

          const bien = (carrito.total_discount || 0) > ahorroAntes;
          this.avisoCodigo(bien ? this.t.codigoOk : this.t.codigoMal, bien);
          if (bien) {
            form.reset();
            document.dispatchEvent(new CustomEvent('cart:update', { detail: { carrito } }));
          }
        })
        .catch(() => this.avisoCodigo(this.t.codigoMal, false))
        .then(() => {
          if (boton) boton.disabled = false;
        });
    }

    /* ---------- Añadir desde las recomendaciones ---------- */

    anadir(id, boton) {
      if (!id || boton.disabled) return;
      boton.disabled = true;

      fetch(this.raiz + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('add'))))
        .then(() => fetch(this.raiz + 'cart.js', { headers: { Accept: 'application/json' } }))
        .then((r) => r.json())
        .then((carrito) => {
          this.pintarCarrito(carrito);
          document.dispatchEvent(new CustomEvent('cart:update', { detail: { carrito } }));
        })
        .catch(() => {
          boton.disabled = false;
        });
    }

    /* ---------- Cambiar de talla o color desde el carrito ----------
       Las variantes no vienen en cart.js, así que se piden al abrir el
       desplegable y solo de ese producto. */

    abrirVariantes(btn) {
      const linea = btn.closest('[data-linea]');
      if (!linea || btn.dataset.cargando) return;
      btn.dataset.cargando = '1';

      fetch(this.raiz + 'products/' + linea.dataset.handle + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('producto'))))
        .then((producto) => {
          const actual = linea.dataset.variante;
          const select = document.createElement('select');
          select.className = 'gwh__linea-select';
          select.setAttribute('aria-label', this.t.cambiar || 'Cambiar');

          producto.variants.forEach((v) => {
            const opcion = document.createElement('option');
            const esActual = String(v.id) === actual;
            opcion.value = v.id;
            opcion.textContent = v.title + (v.available ? '' : ' · ' + (this.t.agotado || 'agotado'));
            opcion.disabled = !v.available && !esActual;
            opcion.selected = esActual;
            select.appendChild(opcion);
          });

          select.addEventListener('change', () => this.cambiarVariante(linea, select.value));
          btn.replaceWith(select);
          select.focus();
        })
        .catch(() => {
          delete btn.dataset.cargando;
        });
    }

    cambiarVariante(linea, nuevoId) {
      const anterior = linea.dataset.variante;
      if (!anterior || String(nuevoId) === anterior) return;

      const contador = linea.querySelector('.gwh__cant span');
      const cantidad = Math.max(1, Number(contador ? contador.textContent : 1));

      // Una sola llamada: se vacía la variante vieja y se pone la nueva
      const updates = {};
      updates[anterior] = 0;
      updates[nuevoId] = cantidad;

      fetch(this.raiz + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates }),
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('update'))))
        .then((carrito) => {
          this.pintarCarrito(carrito);
          document.dispatchEvent(new CustomEvent('cart:update', { detail: { carrito } }));
        })
        .catch(() => this.cargarCarrito());
    }

    /* ---------- Recomendaciones ---------- */

    /* Carrito vacío: si la sección trae plantilla (mensaje + colección
       recomendada) la usamos; si no, el mensaje de siempre. */
    pintarVacio() {
      if (!this.lista) return;

      const plantilla = this.hojaCarrito?.querySelector('[data-gwh-vacio]');
      if (plantilla && plantilla.content) {
        this.lista.replaceChildren(plantilla.content.cloneNode(true));
        return;
      }

      this.lista.innerHTML =
        '<div class="gwh__vacio">' +
        '<strong>' + (this.dataset.textoVacio || 'Tu carrito está vacío') + '</strong>' +
        '<span>' + (this.dataset.textoVacioSub || 'Aún no has añadido nada.') + '</span>' +
        '</div>';
    }

    /* Carrito con cosas: debajo de las líneas, una fila de productos que aún
       no lleva. La sección deja preparados unos cuantos de más, aquí se quitan
       los que ya están dentro y se recorta al número que pidió el merchant. */
    pintarCross(carrito) {
      if (!this.lista) return;

      const plantilla = this.hojaCarrito?.querySelector('[data-gwh-cross]');
      if (!plantilla || !plantilla.content) return;

      const bloque = plantilla.content.cloneNode(true).firstElementChild;
      if (!bloque) return;

      const dentro = new Set((carrito.items || []).map((item) => String(item.product_id)));
      const tope = Number(plantilla.dataset.tope || 4);
      let puestos = 0;

      bloque.querySelectorAll('[data-producto]').forEach((card) => {
        if (dentro.has(card.dataset.producto) || puestos >= tope) card.remove();
        else puestos += 1;
      });

      if (puestos > 0) this.lista.appendChild(bloque);
    }

    /* ---------- Pintado ---------- */

    pintarCarrito(carrito) {
      if (!this.lista) return;

      this.actualizarBurbuja(carrito.item_count);
      if (this.contadorTitulo) this.contadorTitulo.textContent = carrito.item_count;
      this.pintarEnvio(carrito);
      this.pintarNudge(carrito);

      if (!carrito.items.length) {
        this.pintarVacio();
        if (this.pie) this.pie.hidden = true;
        return;
      }

      if (this.pie) this.pie.hidden = false;
      this.pintarResumen(carrito);

      // Al cambiar una cantidad se repinta la lista entera: sin esto,
      // el carrito saltaba arriba del todo cada vez que tocabas el +.
      const alturaScroll = this.lista.scrollTop;

      this.lista.innerHTML = carrito.items
        .map((item, i) => {
          const foto = item.image
            ? '<img class="gwh__linea-foto" src="' + item.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2') +
              '" alt="" loading="lazy" width="68" height="90">'
            : '<div class="gwh__linea-foto"></div>';

          const tieneVariante = item.variant_title && item.variant_title !== 'Default Title';
          const sePuedeCambiar = this.t.cambiarVariante && !item.product_has_only_default_variant;

          let variante = '';
          if (tieneVariante && sePuedeCambiar) {
            variante =
              '<button class="gwh__linea-cambiar" type="button" data-gwh-variante>' +
              this.escapar(item.variant_title) +
              '</button>';
          } else if (tieneVariante) {
            variante = '<span class="gwh__linea-variante">' + this.escapar(item.variant_title) + '</span>';
          }

          // A cantidad 1 el «−» se convierte en papelera: quitar del todo
          // dejaba de ser evidente y hay que bajar a cero a ciegas.
          const quitar =
            item.quantity > 1
              ? '<button type="button" data-gwh-cambia="-1" aria-label="Quitar uno">−</button>'
              : '<button type="button" data-gwh-cambia="-1" aria-label="Eliminar del carrito">' + ICONO_PAPELERA + '</button>';

          const antes =
            item.original_line_price > item.final_line_price
              ? '<span class="gwh__linea-antes">' + this.dinero(item.original_line_price) + '</span>'
              : '';

          return (
            '<div class="gwh__linea" data-linea="' + (i + 1) + '"' +
            ' data-variante="' + item.variant_id + '" data-handle="' + item.handle + '">' +
            '<a href="' + item.url + '">' + foto + '</a>' +
            '<div class="gwh__linea-info">' +
            '<a class="gwh__linea-nombre" href="' + item.url + '">' + this.escapar(item.product_title) + '</a>' +
            variante +
            '<div class="gwh__cant">' +
            quitar +
            '<span>' + item.quantity + '</span>' +
            '<button type="button" data-gwh-cambia="1" aria-label="Añadir uno">+</button>' +
            '</div>' +
            '</div>' +
            '<div class="gwh__linea-lado">' +
            '<span class="gwh__linea-precio">' + this.dinero(item.final_line_price) + '</span>' +
            antes +
            '</div>' +
            '</div>'
          );
        })
        .join('');

      this.pintarCross(carrito);
      this.lista.scrollTop = alturaScroll;
    }

    cambiarLinea(linea, delta) {
      const span = this.lista.querySelector('[data-linea="' + linea + '"] .gwh__cant span');
      const cantidad = Math.max(0, Number(span ? span.textContent : 0) + delta);

      fetch(this.raiz + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: linea, quantity: cantidad }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((carrito) => {
          if (!carrito) return;
          this.pintarCarrito(carrito);
          document.dispatchEvent(new CustomEvent('cart:update', { detail: { carrito } }));
        })
        .catch(() => {});
    }

    actualizarBurbuja(n) {
      const burbuja = this.querySelector('.gwh__contador');
      if (!burbuja) return;
      const numero = burbuja.querySelector('span');
      if (numero) numero.textContent = n;
      burbuja.classList.toggle('tiene', n > 0);
    }

    // Cuando el carrito cambia desde fuera (añadir producto, otro cajón…)
    refrescarCarrito() {
      fetch(this.raiz + 'cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((carrito) => {
          if (!carrito) return;
          this.actualizarBurbuja(carrito.item_count);
          if (this.hojaAbierta === this.hojaCarrito) this.pintarCarrito(carrito);
        })
        .catch(() => {});
    }

    /* ============================================================
       IDIOMA
       La primera visita se abre en el idioma del navegador si la tienda
       lo tiene; a partir de ahí se recuerda, y si elige otro con el
       selector, manda ese. Nunca se aplica a robots ni en el editor.
       ============================================================ */

    iniciarIdioma() {
      const CLAVE = 'gw-idioma';

      const guardar = (valor) => {
        try { localStorage.setItem(CLAVE, valor); } catch (err) {}
      };

      // Al elegir a mano: se guarda y se conserva la búsqueda (?...) de la página
      this.querySelectorAll('[data-gwh-idioma] a[data-iso]').forEach((enlace) => {
        enlace.addEventListener('click', () => {
          guardar(enlace.dataset.iso);
          if (location.search && !enlace.search) enlace.search = location.search;
        });
      });

      const fuente = this.querySelector('[data-gwh-idiomas]');
      if (!fuente) return;

      let datos;
      try { datos = JSON.parse(fuente.textContent); } catch (err) { return; }
      if (!datos.auto || !Array.isArray(datos.idiomas)) return;

      const robot = navigator.webdriver ||
        /bot|crawl|spider|slurp|lighthouse|facebookexternalhit|whatsapp|preview/i.test(navigator.userAgent);
      if (robot) return;

      let elegido = null;
      try { elegido = localStorage.getItem(CLAVE); } catch (err) { return; }

      let destino = null;

      if (elegido) {
        // Ya eligió (o se decidió en su primera visita): se le mantiene en ese idioma
        destino = datos.idiomas.find((i) => i.iso === elegido) || null;
      } else {
        // Primera visita: el primero de los idiomas del navegador que tenga la tienda
        const base = (codigo) => String(codigo || '').toLowerCase().split('-')[0];
        const preferidos = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
        for (const pref of preferidos) {
          destino =
            datos.idiomas.find((i) => i.iso.toLowerCase() === String(pref).toLowerCase()) ||
            datos.idiomas.find((i) => base(i.iso) === base(pref));
          if (destino) break;
        }
        guardar(destino ? destino.iso : datos.actual);
      }

      if (!destino || destino.iso === datos.actual) return;

      // Misma página en el otro idioma: se cambia el prefijo (/, /en, /es…)
      const sinBarra = (r) => (r && r !== '/' ? r.replace(/\/$/, '') : '');
      const raizActual = sinBarra(datos.raizActual);
      let ruta = location.pathname;
      if (raizActual && (ruta === raizActual || ruta.indexOf(raizActual + '/') === 0)) {
        ruta = ruta.slice(raizActual.length) || '/';
      }
      location.replace(sinBarra(destino.raiz) + ruta + location.search + location.hash);
    }

    /* ============================================================
       ANUNCIOS
       ============================================================ */

    iniciarAnuncios() {
      this.barra = this.querySelector('[data-gwh-anuncios]');
      if (!this.barra) return;

      this.anuncios = Array.from(this.barra.querySelectorAll('.gwh__anuncio'));
      this.indice = 0;
      this.intervalo = Number(this.dataset.rotacion || 4500);

      this.barra.querySelectorAll('[data-gwh-anuncio]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.mostrar(Number(btn.dataset.gwhAnuncio));
          this.arrancarAnuncios();
        });
      });

      this.barra.addEventListener('mouseenter', () => this.pararAnuncios());
      this.barra.addEventListener('mouseleave', () => this.arrancarAnuncios());
      this.barra.addEventListener('focusin', () => this.pararAnuncios());
      this.barra.addEventListener('focusout', () => this.arrancarAnuncios());

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pararAnuncios();
        else this.arrancarAnuncios();
      });

      this.arrancarAnuncios();
    }

    mostrar(direccion) {
      if (!this.anuncios || this.anuncios.length < 2) return;

      const total = this.anuncios.length;
      const siguiente = (this.indice + direccion + total) % total;
      if (siguiente === this.indice) return;

      const saliente = this.anuncios[this.indice];
      const entrante = this.anuncios[siguiente];

      saliente.classList.remove('activo');
      saliente.style.transform = 'translateY(' + (-100 * direccion) + '%)';

      entrante.style.transition = 'none';
      entrante.style.transform = 'translateY(' + (100 * direccion) + '%)';
      void entrante.offsetWidth;               // fuerza el reflujo
      entrante.style.transition = '';
      entrante.style.transform = '';
      entrante.classList.add('activo');

      this.indice = siguiente;
    }

    arrancarAnuncios() {
      if (!this.anuncios || this.anuncios.length < 2 || this.menosMovimiento) return;
      this.pararAnuncios();
      this.reloj = setInterval(() => this.mostrar(1), this.intervalo);
    }

    pararAnuncios() {
      if (this.reloj) clearInterval(this.reloj);
      this.reloj = null;
    }

    /* ============================================================
       COLOR SEGÚN EL FONDO
       Con la barra transparente, el logo y los iconos se ponen en
       blanco sobre lo oscuro y en negro sobre lo claro. Se mira lo
       que hay justo debajo, en tres puntos del logo y tres de los
       iconos (por separado: uno puede ir en blanco y otro en negro):
       - un color de fondo: ese color;
       - una foto: lo clara que es ahí mismo, leída de una copia
         pequeña de la imagen (200 px, pedida al CDN de Shopify);
       - un vídeo: no se deja leer, se toma como oscuro;
       - y encima, los velos de las secciones. No reciben clics, así
         que el navegador no los da al preguntar qué hay en un punto:
         se buscan aparte dentro de la sección.
       Se recalcula al desplazar cualquier cosa (ventana, el
       .page-wrapper de Horizon o un carrusel), al cambiar el tamaño
       y cuando termina de cargar una foto.
       ============================================================ */

    iniciarTono() {
      if (!this.hasAttribute('data-color-auto')) return;

      this.grupos = [
        { el: this.querySelector('.gwh__marca'), clase: 'gwh--marca-clara', clara: false },
        { el: this.querySelector('.gwh__acciones'), clase: 'gwh--acciones-claras', clara: false },
      ].filter((g) => g.el);
      if (!this.grupos.length) return;

      this.fotos = new Map();        // src → copia pequeña, o null si no se puede leer
      this.cargandoFotos = new Set();
      this.tonoPendiente = false;
      this.ultimoTono = 0;

      // Como mucho unas 14 veces por segundo: de sobra para que se note al momento
      this.pedirTono = () => {
        if (this.tonoPendiente) return;
        this.tonoPendiente = true;
        const espera = Math.max(0, 70 - (performance.now() - this.ultimoTono));
        setTimeout(() => {
          requestAnimationFrame(() => {
            this.tonoPendiente = false;
            this.ultimoTono = performance.now();
            this.calcularTono();
          });
        }, espera);
      };

      // El desplazamiento no burbujea: en captura se oyen todos
      document.addEventListener('scroll', this.pedirTono, { capture: true, passive: true });
      document.addEventListener('load', this.pedirTono, true);
      window.addEventListener('resize', this.pedirTono, { passive: true });
      window.addEventListener('load', this.pedirTono);

      this.pedirTono();
    }

    pararTono() {
      if (!this.pedirTono) return;
      document.removeEventListener('scroll', this.pedirTono, { capture: true });
      document.removeEventListener('load', this.pedirTono, true);
      window.removeEventListener('resize', this.pedirTono);
      window.removeEventListener('load', this.pedirTono);
    }

    calcularTono() {
      this.grupos.forEach((grupo) => {
        const caja = grupo.el.getBoundingClientRect();
        if (!caja.width || !caja.height) return;

        const y = caja.top + caja.height / 2;
        let suma = 0;
        let cuenta = 0;

        [0.15, 0.5, 0.85].forEach((parte) => {
          const luz = this.luzEn(caja.left + caja.width * parte, y);
          if (luz != null) {
            suma += luz;
            cuenta += 1;
          }
        });

        // Sin datos (una foto aún cargando, por ejemplo): se deja como está
        if (!cuenta) return;

        // El corte está hacia 0,18, donde blanco y negro contrastan igual.
        // Con un margen a cada lado para que no parpadee justo en el límite.
        const luz = suma / cuenta;
        if (!grupo.clara && luz < 0.15) grupo.clara = true;
        else if (grupo.clara && luz > 0.22) grupo.clara = false;

        this.classList.toggle(grupo.clase, grupo.clara);
      });
    }

    /* Luz (0 negro, 1 blanco) de lo que se ve en (x, y) por debajo de la barra.
       null si no se sabe todavía. */
    luzEn(x, y) {
      if (x < 0 || y < 0 || x >= window.innerWidth || y >= window.innerHeight) return null;

      const encima = [];             // capas semitransparentes por encima de la base
      let base = null;               // la primera capa opaca
      let seccion = null;

      for (const el of document.elementsFromPoint(x, y)) {
        if (this.contains(el)) continue;          // la propia cabecera
        if (!seccion) seccion = el.closest('.shopify-section');

        if (el.tagName === 'IMG') {
          const color = this.colorDeFoto(el, x, y);
          if (color === 'pendiente') return null;
          if (color) {
            base = color;
            break;
          }
          continue;
        }

        if (el.tagName === 'VIDEO') {
          base = { r: 20, g: 20, b: 20, a: 1 };   // no se deja leer: se toma como oscuro
          break;
        }

        const fondo = this.leerColor(getComputedStyle(el).backgroundColor);
        if (!fondo || fondo.a <= 0.02) continue;
        if (fondo.a >= 0.98) {
          base = fondo;
          break;
        }
        encima.push(fondo);
      }

      if (!base) {
        const cuerpo = this.leerColor(getComputedStyle(document.body).backgroundColor);
        base = cuerpo && cuerpo.a >= 0.98 ? cuerpo : { r: 255, g: 255, b: 255, a: 1 };
      }

      // Velos de la sección, justo encima de la foto
      if (seccion) {
        seccion.querySelectorAll('[class*="velo"]').forEach((velo) => {
          const capa = this.colorDeVelo(velo, x, y);
          if (capa) encima.push(capa);
        });
      }

      // Se mezclan de abajo arriba sobre la base
      let { r, g, b } = base;
      for (let i = encima.length - 1; i >= 0; i -= 1) {
        const c = encima[i];
        r = r * (1 - c.a) + c.r * c.a;
        g = g * (1 - c.a) + c.g * c.a;
        b = b * (1 - c.a) + c.b * c.a;
      }

      return this.luminancia(r, g, b);
    }

    luminancia(r, g, b) {
      const lineal = (v) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      };
      return 0.2126 * lineal(r) + 0.7152 * lineal(g) + 0.0722 * lineal(b);
    }

    /* rgb(), rgba() y color(srgb …), que es como devuelve el navegador
       los colores hechos con color-mix() */
    leerColor(texto) {
      if (!texto || texto === 'transparent') return null;

      let m = texto.match(/^rgba?\(([^)]+)\)/);
      if (m) {
        const p = m[1].split(/[\s,/]+/).filter(Boolean).map(parseFloat);
        return { r: p[0], g: p[1], b: p[2], a: p.length > 3 ? p[3] : 1 };
      }

      m = texto.match(/^color\(srgb\s+([^)]+)\)/);
      if (m) {
        const p = m[1].split(/[\s/]+/).filter(Boolean).map(parseFloat);
        return { r: p[0] * 255, g: p[1] * 255, b: p[2] * 255, a: p.length > 3 ? p[3] : 1 };
      }

      return null;
    }

    colorDeVelo(velo, x, y) {
      const estilo = getComputedStyle(velo);
      if (estilo.display === 'none' || estilo.visibility === 'hidden') return null;

      const opacidad = Number(estilo.opacity);
      if (!opacidad) return null;

      const caja = velo.getBoundingClientRect();
      if (x < caja.left || x > caja.right || y < caja.top || y > caja.bottom) return null;

      const color = this.colorDeDegradado(estilo.backgroundImage, caja, y) || this.leerColor(estilo.backgroundColor);
      if (!color || color.a <= 0.02) return null;

      return { r: color.r, g: color.g, b: color.b, a: color.a * opacidad };
    }

    /* El color de un linear-gradient a la altura y. Basta para los velos
       de las secciones (de abajo arriba o de arriba abajo). */
    colorDeDegradado(imagen, caja, y) {
      if (!imagen || imagen.indexOf('linear-gradient(') !== 0) return null;

      const dentro = imagen.slice('linear-gradient('.length, imagen.lastIndexOf(')'));
      const desdeAbajo = /^\s*to top\b/.test(dentro);

      const paradas = [];
      const patron = /(rgba?\([^)]*\)|color\([^)]*\))(\s+(-?[\d.]+)%)?/g;
      let m;
      while ((m = patron.exec(dentro))) {
        const color = this.leerColor(m[1]);
        if (color) paradas.push({ color, en: m[3] != null ? Number(m[3]) / 100 : null });
      }
      if (!paradas.length) return null;

      paradas.forEach((p, i) => {
        if (p.en == null) p.en = paradas.length === 1 ? 0 : i / (paradas.length - 1);
      });

      const t = desdeAbajo ? (caja.bottom - y) / caja.height : (y - caja.top) / caja.height;

      if (t <= paradas[0].en) return paradas[0].color;
      for (let i = 1; i < paradas.length; i += 1) {
        const a = paradas[i - 1];
        const b = paradas[i];
        if (t <= b.en) {
          const k = b.en === a.en ? 1 : (t - a.en) / (b.en - a.en);
          return {
            r: a.color.r + (b.color.r - a.color.r) * k,
            g: a.color.g + (b.color.g - a.color.g) * k,
            b: a.color.b + (b.color.b - a.color.b) * k,
            a: a.color.a + (b.color.a - a.color.a) * k,
          };
        }
      }
      return paradas[paradas.length - 1].color;
    }

    /* Color de una foto en (x, y): de su copia pequeña. 'pendiente' si
       aún se está cargando; null si no se puede leer o el punto cae
       fuera de la foto (bandas de object-fit: contain). */
    colorDeFoto(img, x, y) {
      const src = img.currentSrc || img.src;
      if (!src || !img.complete || !img.naturalWidth) return null;

      // La segunda foto que sale al pasar el ratón está escondida con opacidad
      if (Number(getComputedStyle(img).opacity) < 0.5) return null;

      if (!this.fotos.has(src)) {
        this.cargarFoto(src);
        return 'pendiente';
      }

      const copia = this.fotos.get(src);
      if (!copia) return null;

      const punto = this.puntoEnFoto(img, x, y, copia.ancho, copia.alto);
      if (!punto) return null;

      const cx = Math.round(punto[0] * (copia.ancho - 1));
      const cy = Math.round(punto[1] * (copia.alto - 1));

      // La media de un cuadradito de 5 × 5: un píxel suelto engaña
      let r = 0;
      let g = 0;
      let b = 0;
      let n = 0;
      for (let dy = -2; dy <= 2; dy += 1) {
        for (let dx = -2; dx <= 2; dx += 1) {
          const px = cx + dx;
          const py = cy + dy;
          if (px < 0 || py < 0 || px >= copia.ancho || py >= copia.alto) continue;
          const i = (py * copia.ancho + px) * 4;
          r += copia.datos[i];
          g += copia.datos[i + 1];
          b += copia.datos[i + 2];
          n += 1;
        }
      }
      return n ? { r: r / n, g: g / n, b: b / n, a: 1 } : null;
    }

    /* Copia pequeña para leer colores. Se pide aparte, con permiso de
       lectura (crossOrigin), y a 200 px: es una descarga mínima. */
    cargarFoto(src) {
      if (this.cargandoFotos.has(src)) return;
      this.cargandoFotos.add(src);

      let url = src;
      try {
        const u = new URL(src, window.location.href);
        if (u.pathname.indexOf('/cdn/shop/') !== -1 || u.hostname.indexOf('cdn.shopify.com') !== -1) {
          u.searchParams.set('width', '200');
          u.searchParams.delete('height');
          url = u.href;
        }
      } catch (err) {
        url = src;
      }

      const copia = new Image();
      copia.crossOrigin = 'anonymous';
      copia.decoding = 'async';

      copia.onload = () => {
        this.cargandoFotos.delete(src);
        try {
          const ancho = Math.max(1, Math.min(200, copia.naturalWidth));
          const alto = Math.max(1, Math.round((copia.naturalHeight * ancho) / copia.naturalWidth));
          const lienzo = document.createElement('canvas');
          lienzo.width = ancho;
          lienzo.height = alto;
          const ctx = lienzo.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(copia, 0, 0, ancho, alto);
          this.fotos.set(src, { datos: ctx.getImageData(0, 0, ancho, alto).data, ancho, alto });
        } catch (err) {
          this.fotos.set(src, null);   // sin permiso para leerla
        }
        this.pedirTono();
      };

      copia.onerror = () => {
        this.cargandoFotos.delete(src);
        this.fotos.set(src, null);
        this.pedirTono();
      };

      copia.src = url;
    }

    /* Dónde cae (x, y) dentro de la foto, de 0 a 1, teniendo en cuenta
       object-fit y object-position (la foto puede estar recortada) */
    puntoEnFoto(img, x, y, nw, nh) {
      const caja = img.getBoundingClientRect();
      if (!caja.width || !caja.height) return null;

      const estilo = getComputedStyle(img);
      let dw = caja.width;
      let dh = caja.height;
      let ox = 0;
      let oy = 0;

      if (estilo.objectFit === 'cover' || estilo.objectFit === 'contain') {
        const escala = estilo.objectFit === 'cover'
          ? Math.max(caja.width / nw, caja.height / nh)
          : Math.min(caja.width / nw, caja.height / nh);
        dw = nw * escala;
        dh = nh * escala;
        const pos = (estilo.objectPosition || '50% 50%').split(/\s+/);
        ox = this.medida(pos[0], caja.width - dw);
        oy = this.medida(pos[1] || '50%', caja.height - dh);
      }

      const u = (x - caja.left - ox) / dw;
      const v = (y - caja.top - oy) / dh;
      if (u < 0 || u > 1 || v < 0 || v > 1) return null;
      return [u, v];
    }

    medida(valor, libre) {
      if (/%$/.test(valor)) return (libre * parseFloat(valor)) / 100;
      const n = parseFloat(valor);
      return Number.isNaN(n) ? libre / 2 : n;
    }
  }

  customElements.define('gwh-header', GwhHeader);
}